import { useState, useRef, useEffect } from "react";
import { ClockIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

const TimePicker = ({
  value = "",
  onChange,
  placeholder = "Select Time",
  className = "",
  disabled = false,
  error,
  register,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(value ?? "");

  const dropdownRef = useRef(null);
  const timeInputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setSelectedTime(value ?? "");
  }, [value]);

  const handleTimeChange = (e) => {
    const time = e.target.value;
    setSelectedTime(time);
    onChange?.(time);
  };

  const openPicker = () => {
    if (disabled) return;

    setIsOpen(true);

    setTimeout(() => {
      timeInputRef.current?.focus();
      timeInputRef.current?.showPicker?.();
    }, 0);
  };

  const formatTimeForDisplay = (time) => {
    if (!time || time === "00:00") return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <input type="hidden" {...(register || {})} value={selectedTime} />

      <div
        role="button"
        tabIndex={0}
        onClick={openPicker}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && openPicker()
        }
        className={clsx(
          "w-full px-3 py-2 rounded-lg border text-sm",
          "flex items-center gap-2 cursor-pointer",
          error
            ? "border-red-300 text-red-900"
            : "border-gray-300 text-gray-700",
          disabled
            ? "bg-gray-100 cursor-not-allowed"
            : "bg-white hover:border-gray-400",
          className
        )}
      >
        <ClockIcon className="w-5 h-5 text-black flex-shrink-0" />
        <span className={clsx("flex-1", !selectedTime && "text-gray-400")}>
          {selectedTime && selectedTime !== "00:00"
            ? formatTimeForDisplay(selectedTime)
            : placeholder}
        </span>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-[200px]">
          <input
            ref={timeInputRef}
            type="time"
            value={selectedTime}
            onChange={handleTimeChange}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white appearance-none"
          />
        </div>
      )}
    </div>
  );
};
export default TimePicker;
